import React, { useEffect, useState } from 'react';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import { toast } from 'sonner';
import { FileText, ChevronRight, Loader2, Send, Clock, IndianRupee } from 'lucide-react';
import { formatDateTime } from '../../lib/format';

const STATUS_BADGE = {
  Open: 'bg-emerald-50 text-emerald-700',
  Closed: 'bg-slate-100 text-slate-500',
  Awarded: 'bg-violet-50 text-violet-700',
  Pending: 'bg-amber-50 text-amber-700',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return formatDateTime(iso);
}

function inr(v) {
  if (!v) return '₹0';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);
}

export default function VendorRFQs() {
  const { vendorFetch } = useVendorAuth();
  const [rfqs, setRfqs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [rfqDetail, setRfqDetail] = useState(null);
  const [proposal, setProposal] = useState({ notes: '', delivery_days: '', payment_terms: '', validity_days: 30, line_prices: [] });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { vendorFetch('/partner/rfqs').then(d => setRfqs(d.items || [])).catch(() => {}); }, []);

  const openRfq = async (rfq) => {
    setSelected(rfq);
    const d = await vendorFetch(`/partner/rfqs/${rfq.id}`).catch(() => null);
    setRfqDetail(d);
    const items = (d?.items || rfq.items || []);

    // Prefill from existing quote if present
    if (d?.my_quote) {
      const mq = d.my_quote;
      const prefillPrices = items.map(item => {
        const itemKey = item.description || item.item || item.material_name || '';
        const existing = (mq.line_prices || []).find(lp =>
          (lp.item || '').toLowerCase() === itemKey.toLowerCase()
        );
        return { item: itemKey, quantity: item.quantity || 1, unit_price: existing?.unit_price ?? '' };
      });
      setProposal({
        delivery_days: mq.delivery_days ?? '',
        payment_terms: mq.payment_terms ?? '',
        validity_days: mq.validity_days ?? 30,
        notes: mq.notes ?? '',
        line_prices: prefillPrices,
      });
    } else {
      setProposal(p => ({
        ...p,
        line_prices: items.map(item => ({ item: item.description || item.item || item.material_name, quantity: item.quantity || 1, unit_price: '' })),
      }));
    }
  };

  const submitProposal = async () => {
    setSubmitting(true);
    try {
      await vendorFetch(`/partner/rfqs/${selected.id}/proposal`, {
        method: 'POST', body: JSON.stringify({ ...proposal, line_prices: proposal.line_prices.map(l => ({ ...l, unit_price: parseFloat(l.unit_price) || 0 })) }),
      });
      toast.success('Proposal submitted');
      setSelected(null);
      vendorFetch('/partner/rfqs').then(d => setRfqs(d.items || []));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to submit'); }
    setSubmitting(false);
  };

  if (selected && rfqDetail) {
    const mq = rfqDetail.my_quote;
    const history = rfqDetail.my_quote_history || [];
    return (
      <div className="max-w-2xl space-y-5" data-testid="rfq-detail-page">
        <button onClick={() => setSelected(null)} className="text-xs text-slate-500 hover:text-[#3b4872] flex items-center gap-1">← Back to RFQs</button>
        <div>
          <div className="font-bold text-xl">{rfqDetail.rfq_no}</div>
          <div className="text-sm text-slate-500 mt-0.5">{rfqDetail.title || rfqDetail.description}</div>
          {rfqDetail.due_date && <div className="text-xs text-slate-400 mt-1">Due: {rfqDetail.due_date?.slice(0,10)}</div>}
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
          <div className="font-bold text-sm">Items</div>
          {(rfqDetail.items || []).map((it, i) => (
            <div key={i} className="text-sm text-slate-600">{it.description || it.item || it.material_name} — {it.quantity} {it.unit}</div>
          ))}
        </div>

        {/* Current quote summary */}
        {mq && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 space-y-2" data-testid="current-quote-summary">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Current Submitted Quote</span>
              <span className="text-[11px] text-emerald-600">{fmtDate(mq.submitted_at)}</span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div>
                <div className="text-slate-500 text-[10px] uppercase tracking-wider mb-0.5">Total Value</div>
                <div className="font-bold text-emerald-700 text-base">{inr(mq.total_value)}</div>
              </div>
              <div>
                <div className="text-slate-500 text-[10px] uppercase tracking-wider mb-0.5">Delivery</div>
                <div className="font-semibold">{mq.delivery_days ? `${mq.delivery_days} days` : '—'}</div>
              </div>
              <div>
                <div className="text-slate-500 text-[10px] uppercase tracking-wider mb-0.5">Payment Terms</div>
                <div className="font-semibold">{mq.payment_terms || '—'}</div>
              </div>
            </div>
            {mq.validity_days && <div className="text-[11px] text-slate-500">Validity: {mq.validity_days} days · {mq.notes && `Notes: ${mq.notes}`}</div>}
            <div className="text-[11px] text-emerald-700 font-semibold mt-1">Editing below will replace this quote.</div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-4">
          <div className="font-bold text-sm">{mq ? 'Revise Your Proposal' : 'Your Proposal'}</div>
          {proposal.line_prices.map((lp, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="flex-1 text-xs text-slate-700">{lp.item}</div>
              <input type="number" placeholder="Unit price (₹)" value={lp.unit_price}
                onChange={e => { const lps = [...proposal.line_prices]; lps[i] = { ...lps[i], unit_price: e.target.value }; setProposal({ ...proposal, line_prices: lps }); }}
                className="w-32 border border-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-[#3b4872]" />
            </div>
          ))}
          <div className="grid sm:grid-cols-3 gap-3">
            <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Delivery (days)</label><input type="number" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={proposal.delivery_days} onChange={e => setProposal({ ...proposal, delivery_days: e.target.value })} /></div>
            <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Payment Terms</label><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={proposal.payment_terms} onChange={e => setProposal({ ...proposal, payment_terms: e.target.value })} placeholder="e.g. 30 days" /></div>
            <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Validity (days)</label><input type="number" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={proposal.validity_days} onChange={e => setProposal({ ...proposal, validity_days: e.target.value })} /></div>
          </div>
          <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Notes</label><textarea rows={2} className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872] resize-none" value={proposal.notes} onChange={e => setProposal({ ...proposal, notes: e.target.value })} /></div>
          <button onClick={submitProposal} disabled={submitting}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#3b4872] text-white rounded-sm text-sm font-bold disabled:opacity-50" data-testid="submit-proposal-btn">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {submitting ? 'Submitting…' : (mq ? 'Revise & Resubmit' : 'Submit Proposal')}
          </button>
        </div>

        {/* Quote history */}
        {history.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="quote-history-section">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={14} className="text-slate-400" />
              <span className="font-bold text-sm text-slate-700">Your Quote History</span>
              <span className="text-[11px] text-slate-400">({history.length} previous version{history.length !== 1 ? 's' : ''})</span>
            </div>
            <div className="space-y-2">
              {history.map((h, i) => (
                <div key={i} className="border border-slate-100 rounded-sm p-3 bg-slate-50/40" data-testid={`quote-history-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-slate-500">Submitted {fmtDate(h.submitted_at)}</span>
                    <span className="text-[10px] text-slate-400">Superseded {fmtDate(h.superseded_at)}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div><span className="text-slate-400">Total: </span><span className="font-semibold">{inr(h.total_value)}</span></div>
                    <div><span className="text-slate-400">Delivery: </span><span className="font-semibold">{h.delivery_days ? `${h.delivery_days}d` : '—'}</span></div>
                    <div><span className="text-slate-400">Terms: </span><span className="font-semibold">{h.payment_terms || '—'}</span></div>
                  </div>
                  {h.notes && <div className="text-[11px] text-slate-500 mt-1 italic">"{h.notes}"</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="vendor-rfqs-page">
      <div>
        <h1 className="font-bold text-xl text-slate-900">My RFQs</h1>
        <p className="text-sm text-slate-500 mt-0.5">RFQs you have been invited to quote on.</p>
      </div>
      {rfqs.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-400">
          <FileText size={28} className="mx-auto mb-3 opacity-40" />
          <div className="font-semibold">No RFQs yet</div>
          <div className="text-xs mt-1">Ellora EPC will invite you to quote when there's a matching requirement.</div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100">
          {rfqs.map(rfq => (
            <button key={rfq.id} onClick={() => openRfq(rfq)}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-50 text-left" data-testid={`rfq-row-${rfq.rfq_no}`}>
              <div>
                <div className="font-semibold text-sm">{rfq.rfq_no} — {rfq.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">{(rfq.items || []).length} items · due {rfq.due_date?.slice(0,10) || '—'}</div>
                {rfq.vendor_quoted && <div className="text-[11px] text-emerald-600 font-bold mt-0.5">✓ Proposal submitted · {inr(rfq.my_quote?.total_value)}</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${STATUS_BADGE[rfq.status] || 'bg-slate-100 text-slate-600'}`}>{rfq.status}</span>
                <ChevronRight size={15} className="text-slate-400" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
